import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle2, Calendar, MapPin, Shield, FileText, Users, ArrowLeft, Award, Clock, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { courses } from '../lib/data';
export function CourseDetailPage() {
  const {
    id
  } = useParams();
  const course = courses.find(c => c.id === id);
  if (!course) {
    return <div className="min-h-[60vh] flex flex-col items-center justify-center bg-cream-100" data-id="element-450">
        <h1 className="text-3xl font-display font-bold text-forest-900 mb-4" data-id="element-451">
          Course not found
        </h1>
        <Link to="/courses" data-id="element-452">
          <Button data-id="element-453">Back to Courses</Button>
        </Link>
      </div>;
  }
  return <div className="min-h-screen bg-gradient-to-br from-cream-100 via-white to-forest-50" data-id="element-454">
      {/* Breadcrumb */}
      <div className="bg-white border-b-2 border-forest-100" data-id="element-455">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6" data-id="element-456">
          <Link to="/courses" className="inline-flex items-center text-sm font-semibold text-forest-700 hover:text-ember-600 transition-colors group" data-id="element-457">
            <ArrowLeft className="w-4 h-4 mr-2 group-hover:-translate-x-1 transition-transform" data-id="element-458" />
            Back to all courses
          </Link>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16" data-id="element-459">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12" data-id="element-460">
          {/* Main Content */}
          <div className="lg:col-span-2" data-id="element-461">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6
          }} className="mb-10" data-id="element-462">
              <Badge variant="gradient" className="mb-6" data-id="element-463">
                <Sparkles className="w-3 h-3 mr-1" data-id="element-464" />
                {course.category}
              </Badge>
              <h1 className="text-4xl md:text-5xl font-display font-bold text-forest-900 mb-6" data-id="element-465">
                {course.title}
              </h1>
              <p className="text-xl text-gray-700 leading-relaxed" data-id="element-466">
                {course.description}
              </p>
            </motion.div>

            {/* Course Modules */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.2
          }} className="mb-12" data-id="element-467">
              <h3 className="text-3xl font-display font-bold text-forest-900 mb-8 flex items-center" data-id="element-468">
                <div className="w-10 h-10 bg-gradient-to-br from-forest-600 to-forest-700 rounded-xl flex items-center justify-center mr-3" data-id="element-469">
                  <FileText className="w-5 h-5 text-white" data-id="element-470" />
                </div>
                Course Modules
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-471">
                {course.modules.map((module, idx) => <motion.div key={idx} initial={{
                opacity: 0,
                x: -20
              }} animate={{
                opacity: 1,
                x: 0
              }} transition={{
                delay: 0.3 + idx * 0.1
              }} className="flex items-start p-5 rounded-2xl bg-white border-2 border-forest-100 hover:border-forest-300 hover:shadow-lg transition-all" data-id="element-472">
                    <div className="w-6 h-6 rounded-full bg-gradient-to-br from-forest-600 to-forest-700 flex items-center justify-center mr-4 mt-0.5 flex-shrink-0" data-id="element-473">
                      <CheckCircle2 className="w-3 h-3 text-white" data-id="element-474" />
                    </div>
                    <span className="text-gray-800 font-semibold leading-relaxed" data-id="element-475">
                      {module}
                    </span>
                  </motion.div>)}
              </div>
            </motion.div>

            {/* Certification Section */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.4
          }} className="bg-gradient-to-br from-forest-900 to-forest-800 rounded-3xl p-10 text-white relative overflow-hidden" data-id="element-476">
              <div className="absolute top-0 right-0 w-64 h-64 bg-ember-500/20 rounded-full blur-3xl" data-id="element-477"></div>
              <div className="relative z-10" data-id="element-478">
                <h3 className="text-2xl font-display font-bold mb-8 flex items-center" data-id="element-479">
                  <Award className="w-8 h-8 mr-3 text-ember-400" data-id="element-480" />
                  Certification & Compliance
                </h3>
                <div className="space-y-6" data-id="element-481">
                  <div className="flex items-start" data-id="element-482">
                    <Shield className="w-7 h-7 text-ember-400 mr-4 mt-1 flex-shrink-0" data-id="element-483" />
                    <div data-id="element-484">
                      <h4 className="font-bold text-lg mb-2" data-id="element-485">FSAI Compliant</h4>
                      <p className="text-forest-200 leading-relaxed" data-id="element-486">
                        This course follows the guidelines set out by the Food
                        Safety Authority of Ireland and meets all regulatory
                        requirements.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start" data-id="element-487">
                    <FileText className="w-7 h-7 text-ember-400 mr-4 mt-1 flex-shrink-0" data-id="element-488" />
                    <div data-id="element-489">
                      <h4 className="font-bold text-lg mb-2" data-id="element-490">
                        Official Certification
                      </h4>
                      <p className="text-forest-200 leading-relaxed" data-id="element-491">
                        Upon successful completion, participants receive an
                        official certificate valid for 3 years, recognized
                        across Ireland.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1" data-id="element-492">
            <motion.div initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 0.6,
            delay: 0.2
          }} className="sticky top-28" data-id="element-493">
              <div className="bg-white rounded-3xl shadow-2xl border-2 border-forest-100 overflow-hidden" data-id="element-494">
                <div className="bg-gradient-to-br from-forest-800 to-forest-900 p-8 text-white" data-id="element-495">
                  <p className="text-forest-300 text-sm font-bold uppercase tracking-wider mb-2" data-id="element-496">
                    Course Fee
                  </p>
                  <div className="flex items-baseline mb-2" data-id="element-497">
                    <span className="text-5xl font-display font-bold" data-id="element-498">
                      {course.price}
                    </span>
                    <span className="text-forest-300 ml-3 text-sm" data-id="element-499">
                      per person
                    </span>
                  </div>
                  <p className="text-forest-200 text-xs" data-id="element-500">
                    Volume discounts available for teams
                  </p>
                </div>

                <div className="p-8 space-y-6" data-id="element-501">
                  <div className="space-y-4" data-id="element-502">
                    <div className="flex items-center text-gray-700" data-id="element-503">
                      <div className="w-10 h-10 bg-forest-100 rounded-xl flex items-center justify-center mr-3" data-id="element-504">
                        <Clock className="w-5 h-5 text-forest-700" data-id="element-505" />
                      </div>
                      <div data-id="element-506">
                        <p className="text-xs text-gray-500 font-medium" data-id="element-507">
                          Duration
                        </p>
                        <p className="font-bold" data-id="element-508">{course.duration}</p>
                      </div>
                    </div>
                    <div className="flex items-center text-gray-700" data-id="element-509">
                      <div className="w-10 h-10 bg-forest-100 rounded-xl flex items-center justify-center mr-3" data-id="element-510">
                        <MapPin className="w-5 h-5 text-forest-700" data-id="element-511" />
                      </div>
                      <div data-id="element-512">
                        <p className="text-xs text-gray-500 font-medium" data-id="element-513">
                          Format
                        </p>
                        <p className="font-bold" data-id="element-514">Online · Self-Paced</p>
                      </div>
                    </div>
                    <div className="flex items-center text-gray-700" data-id="element-515">
                      <div className="w-10 h-10 bg-forest-100 rounded-xl flex items-center justify-center mr-3" data-id="element-516">
                        <Users className="w-5 h-5 text-forest-700" data-id="element-517" />
                      </div>
                      <div data-id="element-518">
                        <p className="text-xs text-gray-500 font-medium" data-id="element-519">
                          Group Options
                        </p>
                        <p className="font-bold" data-id="element-520">Discounts available</p>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-3 pt-6 border-t-2 border-forest-100" data-id="element-521">
                    <Button fullWidth size="lg" variant="primary" data-id="element-522">
                      Book Now
                    </Button>
                    <Button fullWidth size="lg" variant="outline" data-id="element-523">
                      Enquire for Team
                    </Button>
                  </div>

                  <p className="text-xs text-center text-gray-500 pt-4" data-id="element-524">
                    🔒 Secure payment · Instant access
                  </p>
                </div>
              </div>

              <div className="mt-6 bg-gradient-to-br from-ember-50 to-forest-50 rounded-2xl p-6 border-2 border-ember-200" data-id="element-525">
                <h4 className="font-display font-bold text-forest-900 mb-2 text-lg" data-id="element-526">
                  Training your team?
                </h4>
                <p className="text-sm text-gray-700 mb-4 leading-relaxed" data-id="element-527">
                  Enrol multiple staff members with our group management
                  platform. Track progress and download certificates from one
                  dashboard.
                </p>
                <Link to="/corporate" className="text-sm font-bold text-forest-700 hover:text-ember-600 underline transition-colors" data-id="element-528">
                  Explore Team Accounts →
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>;
}